import React, { useRef } from 'react';
import { Drawer, Box, Typography, IconButton, Divider, Button, Paper, useTheme, useMediaQuery, Table, TableBody, TableCell, TableContainer, TableRow } from '@mui/material';
import { Icon } from '@iconify/react';
import { useToast } from './ui/ToastProvider';
interface Transaction {
  id: string;
  mtoRef: string;
  bankRef: string;
  mtoName: string;
  remitterName: string;
  beneficiaryName: string;
  beneficiaryIBAN: string;
  amountFCY: string;
  currencyFCY: string;
  amountPKR: string;
  purposeCode: string;
  purposeText: string;
  payoutType: string;
  corridor: string;
  createdAt: string;
  ePRCNo?: string;
  ePRCDate?: string;
}
interface EPRCViewerDrawerProps {
  open: boolean;
  onClose: () => void;
  transaction: Transaction;
}
const EPRCViewerDrawer: React.FC<EPRCViewerDrawerProps> = ({
  open,
  onClose,
  transaction
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const {
    showToast
  } = useToast();
  const printRef = useRef<HTMLDivElement>(null);
  const handlePrint = () => {
    if (printRef.current) {
      const printWindow = window.open('', '', 'height=600,width=800');
      if (printWindow) {
        printWindow.document.write('<html><head><title>e-PRC Certificate</title>');
        printWindow.document.write('<style>{`');
        printWindow.document.write(`
          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
          }
          body { 
            font-family: Arial, sans-serif; 
            margin: 0;
            padding: 20mm;
          }
          .certificate-container {
            border: 2px solid #000 !important;
            padding: 30px;
            page-break-inside: avoid;
            page-break-after: always;
          }
          img {
            max-width: 100% !important;
            height: auto !important;
          }
          table { 
            border-collapse: collapse !important; 
            width: 100% !important; 
            margin-bottom: 15px !important;
            border: 1px solid #000 !important;
          }
          td, th { 
            border: 1px solid #000 !important; 
            padding: 8px !important; 
            font-size: 11px !important;
          }
          .header { 
            text-align: center; 
            margin-bottom: 20px; 
            padding-bottom: 15px;
            border-bottom: 2px solid #000 !important;
          }
          .header h1 { 
            font-size: 18px; 
            margin: 0 0 5px 0;
            font-weight: bold;
          }
          .header p { 
            font-size: 13px; 
            margin: 0;
          }
          .section-header {
            background-color: #f5f5f5 !important;
            font-weight: bold !important;
          }
          .note {
            font-size: 10px;
            font-style: italic;
            margin-top: 10px;
          }
          .cert-text {
            font-size: 11px;
            line-height: 1.6;
            margin-bottom: 15px;
          }
          @page {
            size: A4 portrait;
            margin: 0;
          }
          @media print {
            body {
              margin: 0;
              padding: 20mm;
            }
            .certificate-container {
              border: 2px solid #000 !important;
              page-break-after: always;
              page-break-inside: avoid;
            }
            img {
              max-width: 100% !important;
              height: auto !important;
            }
            table, td, th {
              border: 1px solid #000 !important;
            }
            .section-header {
              background-color: #f5f5f5 !important;
            }
          }
        `);
        printWindow.document.write('`}</style></head><body>');
        printWindow.document.write('<div class="certificate-container">');
        printWindow.document.write(printRef.current.innerHTML);
        printWindow.document.write('</div>');
        printWindow.document.write('</body></html>');
        printWindow.document.close();
        printWindow.focus();
        setTimeout(() => {
          printWindow.print();
          printWindow.close();
        }, 250);
      }
    }
  };
  const handleDownload = () => {
    showToast('Use Print button and select "Save as PDF" in the print dialog', 'info', 'Download as PDF', 5000);
    handlePrint();
  };
  // Extract country from corridor (e.g., "USA-PAK" -> "USA")
  const getRemitterCountry = () => {
    const parts = transaction.corridor.split('-');
    return parts[0] || 'N/A';
  };
  // Get country code (simplified - in real app would use proper mapping)
  const getCountryCode = () => {
    const country = getRemitterCountry();
    const codes: Record<string, string> = {
      USA: 'US',
      UAE: 'AE',
      UK: 'GB',
      SAU: 'SA'
    };
    return codes[country] || country;
  };
  return <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{
    sx: {
      width: isMobile ? '100%' : 800,
      maxWidth: '100%'
    }
  }} data-id="element-841">
      <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%'
    }} data-id="element-842">
        {/* Header */}
        <Box sx={{
        p: 3,
        pb: 2,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }} data-id="element-843">
          <Box data-id="element-844">
            <Typography variant="h6" fontWeight={600} data-id="element-845">
              e-PRC Certificate
            </Typography>
            <Typography variant="body2" color="text.secondary" data-id="element-846">
              {transaction.ePRCNo}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small" data-id="element-847">
            <Icon icon="lucide:x" width={20} data-id="element-848" />
          </IconButton>
        </Box>
        <Divider data-id="element-849" />

        {/* e-PRC Content */}
        <Box sx={{
        flex: 1,
        overflow: 'auto',
        p: 4
      }} data-id="element-850">
          <Box ref={printRef} sx={{
          backgroundColor: 'white'
        }} data-id="element-851">
            <Box sx={{
            p: 4,
            border: '2px solid #000',
            borderRadius: 0
          }} data-id="element-852">
              {/* Header */}
              <Box className="header" sx={{
              textAlign: 'center',
              mb: 2,
              pb: 2,
              borderBottom: '2px solid #000'
            }} data-id="element-853">
                <Typography component="p" sx={{
                fontSize: '0.75rem',
                textAlign: 'right',
                mb: 1
              }} data-id="element-854">
                  Appendix V-148
                </Typography>
                <Box sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                mb: 1
              }} data-id="element-855">
                  <Box component="img" src="https://cdn.magicpatterns.com/uploads/hpRhGyJFvywHpMLciM7oAL/bank-islami-logo.png" alt="BankIslami Logo" sx={{
                  height: 60,
                  width: 'auto'
                }} data-id="element-856" />
                </Box>
                <Typography component="h1" sx={{
                fontSize: '1.1rem',
                fontWeight: 700,
                mt: 1
              }} data-id="element-857">
                  Proceeds Realization Certificate (PRC)
                </Typography>
              </Box>

              {/* e-PRC Number and Date */}
              <TableContainer sx={{
              mb: 2
            }} data-id="element-858">
                <Table size="small" sx={{
                border: '1px solid #000'
              }} data-id="element-859">
                  <TableBody data-id="element-860">
                    <TableRow data-id="element-861">
                      <TableCell colSpan={3} sx={{
                      border: '1px solid #000',
                      fontWeight: 600,
                      fontSize: '0.8rem',
                      p: 1,
                      textAlign: 'right'
                    }} data-id="element-862">
                        e-PRC No. (Unique ID)
                      </TableCell>
                    </TableRow>
                    <TableRow data-id="element-863">
                      <TableCell colSpan={3} sx={{
                      border: '1px solid #000',
                      fontSize: '0.8rem',
                      p: 1,
                      textAlign: 'right'
                    }} data-id="element-864">
                        {transaction.ePRCNo}
                      </TableCell>
                    </TableRow>
                    <TableRow data-id="element-865">
                      <TableCell colSpan={3} sx={{
                      border: '1px solid #000',
                      fontWeight: 600,
                      fontSize: '0.8rem',
                      p: 1,
                      textAlign: 'right'
                    }} data-id="element-866">
                        Date of e-PRC:
                      </TableCell>
                    </TableRow>
                    <TableRow data-id="element-867">
                      <TableCell colSpan={3} sx={{
                      border: '1px solid #000',
                      fontSize: '0.8rem',
                      p: 1,
                      textAlign: 'right'
                    }} data-id="element-868">
                        {new Date(transaction.ePRCDate || '').toLocaleDateString('en-GB')}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Remitter and Beneficiary Details */}
              <TableContainer sx={{
              mb: 2
            }} data-id="element-869">
                <Table size="small" sx={{
                border: '1px solid #000'
              }} data-id="element-870">
                  <TableBody data-id="element-871">
                    <TableRow data-id="element-872">
                      <TableCell colSpan={2} className="section-header" sx={{
                      border: '1px solid #000',
                      fontWeight: 600,
                      backgroundColor: '#f5f5f5',
                      fontSize: '0.8rem',
                      p: 1
                    }} data-id="element-873">
                        Remitter Details:
                      </TableCell>
                      <TableCell colSpan={2} className="section-header" sx={{
                      border: '1px solid #000',
                      fontWeight: 600,
                      backgroundColor: '#f5f5f5',
                      fontSize: '0.8rem',
                      p: 1
                    }} data-id="element-874">
                        Beneficiary Details:
                      </TableCell>
                    </TableRow>
                    <TableRow data-id="element-875">
                      <TableCell sx={{
                      border: '1px solid #000',
                      fontWeight: 600,
                      width: '25%',
                      fontSize: '0.75rem',
                      p: 1
                    }} data-id="element-876">
                        Name of Person/ Entity
                      </TableCell>
                      <TableCell sx={{
                      border: '1px solid #000',
                      width: '25%',
                      fontSize: '0.75rem',
                      p: 1
                    }} data-id="element-877">
                        {transaction.remitterName}
                      </TableCell>
                      <TableCell sx={{
                      border: '1px solid #000',
                      fontWeight: 600,
                      width: '25%',
                      fontSize: '0.75rem',
                      p: 1
                    }} data-id="element-878">
                        Name of Person / Entity
                      </TableCell>
                      <TableCell sx={{
                      border: '1px solid #000',
                      width: '25%',
                      fontSize: '0.75rem',
                      p: 1
                    }} data-id="element-879">
                        {transaction.beneficiaryName}
                      </TableCell>
                    </TableRow>
                    <TableRow data-id="element-880">
                      <TableCell sx={{
                      border: '1px solid #000',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      p: 1
                    }} data-id="element-881">
                        NICOP/ CNIC/ Passport No./ POC/ Entity Registration No./
                        Any other Unique ID
                      </TableCell>
                      <TableCell sx={{
                      border: '1px solid #000',
                      fontSize: '0.75rem',
                      p: 1
                    }} data-id="element-882"></TableCell>
                      <TableCell sx={{
                      border: '1px solid #000',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      p: 1
                    }} data-id="element-883">
                        NICOP / CNIC / POC Passport No. /NTN
                      </TableCell>
                      <TableCell sx={{
                      border: '1px solid #000',
                      fontSize: '0.75rem',
                      p: 1
                    }} data-id="element-884"></TableCell>
                    </TableRow>
                    <TableRow data-id="element-885">
                      <TableCell sx={{
                      border: '1px solid #000',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      p: 1
                    }} data-id="element-886">
                        IBAN/ Bank Account No. (if applicable)
                      </TableCell>
                      <TableCell sx={{
                      border: '1px solid #000',
                      fontSize: '0.75rem',
                      p: 1
                    }} data-id="element-887"></TableCell>
                      <TableCell sx={{
                      border: '1px solid #000',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      p: 1
                    }} data-id="element-888">
                        IBAN/ Bank Account No.
                      </TableCell>
                      <TableCell sx={{
                      border: '1px solid #000',
                      fontSize: '0.75rem',
                      p: 1
                    }} data-id="element-889">
                        {transaction.beneficiaryIBAN}
                      </TableCell>
                    </TableRow>
                    <TableRow data-id="element-890">
                      <TableCell sx={{
                      border: '1px solid #000',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      p: 1
                    }} data-id="element-891">
                        Name of Remitting (originating) Financial Institution
                      </TableCell>
                      <TableCell sx={{
                      border: '1px solid #000',
                      fontSize: '0.75rem',
                      p: 1
                    }} data-id="element-892">
                        {transaction.mtoName}
                      </TableCell>
                      <TableCell sx={{
                      border: '1px solid #000',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      p: 1
                    }} data-id="element-893">
                        Name of the Bank:
                      </TableCell>
                      <TableCell sx={{
                      border: '1px solid #000',
                      fontSize: '0.75rem',
                      p: 1
                    }} data-id="element-894"></TableCell>
                    </TableRow>
                    <TableRow data-id="element-895">
                      <TableCell sx={{
                      border: '1px solid #000',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      p: 1
                    }} data-id="element-896">
                        Name of Country
                      </TableCell>
                      <TableCell sx={{
                      border: '1px solid #000',
                      fontSize: '0.75rem',
                      p: 1
                    }} data-id="element-897">
                        {getRemitterCountry()}
                      </TableCell>
                      <TableCell colSpan={2} sx={{
                      border: '1px solid #000'
                    }} data-id="element-898" />
                    </TableRow>
                    <TableRow data-id="element-899">
                      <TableCell sx={{
                      border: '1px solid #000',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      p: 1
                    }} data-id="element-900">
                        Country Code (as per ITRS)
                      </TableCell>
                      <TableCell sx={{
                      border: '1px solid #000',
                      fontSize: '0.75rem',
                      p: 1
                    }} data-id="element-901">
                        {getCountryCode()}
                      </TableCell>
                      <TableCell colSpan={2} sx={{
                      border: '1px solid #000'
                    }} data-id="element-902" />
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Certification Text */}
              <Box className="cert-text" sx={{
              mb: 2,
              fontSize: '0.75rem',
              lineHeight: 1.5
            }} data-id="element-903">
                This is to certify that we have received the following amounts
                in favor of the Beneficiary, as per the details given below:
              </Box>

              {/* Transaction Details */}
              <TableContainer sx={{
              mb: 2
            }} data-id="element-904">
                <Table size="small" sx={{
                border: '1px solid #000'
              }} data-id="element-905">
                  <TableBody data-id="element-906">
                    <TableRow data-id="element-907">
                      <TableCell colSpan={5} className="section-header" sx={{
                      border: '1px solid #000',
                      fontWeight: 600,
                      backgroundColor: '#f5f5f5',
                      fontSize: '0.8rem',
                      p: 1
                    }} data-id="element-908">
                        Transaction Detail:
                      </TableCell>
                    </TableRow>
                    <TableRow data-id="element-909">
                      <TableCell sx={{
                      border: '1px solid #000',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      p: 1
                    }} data-id="element-910">
                        Date of Realization of Proceeds
                      </TableCell>
                      <TableCell colSpan={4} sx={{
                      border: '1px solid #000',
                      fontSize: '0.75rem',
                      p: 1
                    }} data-id="element-911">
                        {new Date(transaction.createdAt).toLocaleDateString('en-GB')}{' '}
                        (DD-MM-YYYY)
                      </TableCell>
                    </TableRow>
                    <TableRow data-id="element-912">
                      <TableCell rowSpan={3} sx={{
                      border: '1px solid #000',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      p: 1,
                      verticalAlign: 'top'
                    }} data-id="element-913">
                        Amount
                      </TableCell>
                      <TableCell sx={{
                      border: '1px solid #000',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      p: 1,
                      textAlign: 'center'
                    }} data-id="element-914">
                        Total Proceeds Received in FCY*
                      </TableCell>
                      <TableCell sx={{
                      border: '1px solid #000',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      p: 1,
                      textAlign: 'center'
                    }} data-id="element-915">
                        FCY Retained in ESFCA (if applicable)
                      </TableCell>
                      <TableCell colSpan={2} sx={{
                      border: '1px solid #000',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      p: 1,
                      textAlign: 'center'
                    }} data-id="element-916">
                        Converted to Local
                      </TableCell>
                    </TableRow>
                    <TableRow data-id="element-917">
                      <TableCell rowSpan={2} sx={{
                      border: '1px solid #000',
                      fontSize: '0.75rem',
                      p: 1,
                      textAlign: 'center'
                    }} data-id="element-918">
                        {transaction.currencyFCY} {transaction.amountFCY}
                      </TableCell>
                      <TableCell rowSpan={2} sx={{
                      border: '1px solid #000',
                      fontSize: '0.75rem',
                      p: 1,
                      textAlign: 'center'
                    }} data-id="element-919"></TableCell>
                      <TableCell sx={{
                      border: '1px solid #000',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      p: 1,
                      textAlign: 'center'
                    }} data-id="element-920">
                        Amount in FCY
                      </TableCell>
                      <TableCell sx={{
                      border: '1px solid #000',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      p: 1,
                      textAlign: 'center'
                    }} data-id="element-921">
                        Rate of Conversion*
                      </TableCell>
                      <TableCell sx={{
                      border: '1px solid #000',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      p: 1,
                      textAlign: 'center'
                    }} data-id="element-922">
                        Amt. in Local
                      </TableCell>
                    </TableRow>
                    <TableRow data-id="element-923">
                      <TableCell sx={{
                      border: '1px solid #000',
                      fontSize: '0.75rem',
                      p: 1,
                      textAlign: 'center'
                    }} data-id="element-924">
                        {transaction.currencyFCY} {transaction.amountFCY}
                      </TableCell>
                      <TableCell sx={{
                      border: '1px solid #000',
                      fontSize: '0.75rem',
                      p: 1,
                      textAlign: 'center'
                    }} data-id="element-925">
                        {(parseFloat(transaction.amountPKR) / parseFloat(transaction.amountFCY)).toFixed(2)}
                      </TableCell>
                      <TableCell sx={{
                      border: '1px solid #000',
                      fontSize: '0.75rem',
                      p: 1,
                      textAlign: 'center'
                    }} data-id="element-926">
                        {parseFloat(transaction.amountPKR).toFixed(2)}
                      </TableCell>
                    </TableRow>
                    <TableRow data-id="element-927">
                      <TableCell sx={{
                      border: '1px solid #000',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      p: 1
                    }} data-id="element-928">
                        Purpose of Remittance
                      </TableCell>
                      <TableCell colSpan={4} sx={{
                      border: '1px solid #000',
                      fontSize: '0.75rem',
                      p: 1
                    }} data-id="element-929">
                        {transaction.purposeText}
                      </TableCell>
                    </TableRow>
                    <TableRow data-id="element-930">
                      <TableCell sx={{
                      border: '1px solid #000',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      p: 1
                    }} data-id="element-931">
                        Purpose Code (as per ITRS)
                      </TableCell>
                      <TableCell colSpan={4} sx={{
                      border: '1px solid #000',
                      fontSize: '0.75rem',
                      p: 1
                    }} data-id="element-932">
                        {transaction.purposeCode}
                      </TableCell>
                    </TableRow>
                    <TableRow data-id="element-933">
                      <TableCell sx={{
                      border: '1px solid #000',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      p: 1
                    }} data-id="element-934">
                        Transaction Reference No.
                      </TableCell>
                      <TableCell colSpan={4} sx={{
                      border: '1px solid #000',
                      fontSize: '0.75rem',
                      p: 1
                    }} data-id="element-935">
                        {transaction.bankRef}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Footer Notes */}
              <Box sx={{
              mb: 2,
              pl: 2
            }} data-id="element-936">
                <Box sx={{
                fontSize: '0.7rem',
                mb: 1
              }} data-id="element-937">
                  i. The above transaction has been/will be reported to SBP /
                  SBP-BSC in Schedule ------------- of ITRS for the period:
                  month-------year--------.
                </Box>
                <Box sx={{
                fontSize: '0.7rem',
                mb: 2
              }} data-id="element-938">
                  ii. The authorized officers of the verifying agency may verify
                  the authenticity of the remittance on our Bank's portal at
                  __________________________.
                </Box>
              </Box>

              <Box className="note" sx={{
              fontSize: '0.7rem',
              fontStyle: 'italic',
              mb: 2
            }} data-id="element-939">
                Note: This is a system-generated document and does not require
                any signature.
              </Box>

              <Box sx={{
              fontSize: '0.7rem',
              fontStyle: 'italic',
              mb: 2
            }} data-id="element-940">
                *N/A in case the payment is received (1) either through Vostro
                account of the remitting financial institution and the rate of
                conversion has been applied by the remitting institution abroad,
                or (2) through Roshan Digital Account.
              </Box>

              <Box sx={{
              textAlign: 'center',
              fontSize: '1rem',
              fontWeight: 600,
              mb: 3
            }} data-id="element-941">
                *****
              </Box>

              {/* Guidance Section */}
              <Box sx={{
              fontSize: '0.7rem',
              mb: 1
            }} data-id="element-942">
                <Typography sx={{
                fontSize: '0.75rem',
                fontWeight: 600,
                textDecoration: 'underline',
                mb: 1
              }} data-id="element-943">
                  Guidance for Authorized Dealers:
                </Typography>
                <Box component="ol" sx={{
                pl: 2,
                m: 0
              }} data-id="element-944">
                  <Box component="li" sx={{
                  mb: 0.5
                }} data-id="element-945">
                    The banks should not allow the exporters to buy FCY again
                    for credit to the ESFCAs, if the exporter has voluntarily
                    converted the amount into local currency.
                  </Box>
                  <Box component="li" data-id="element-946">
                    e-PRC would be issued only once for each incoming
                    remittance.
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Action Buttons */}
        <Divider data-id="element-947" />
        <Box sx={{
        p: 3,
        display: 'flex',
        gap: 2
      }} data-id="element-948">
          <Button variant="outlined" startIcon={<Icon icon="lucide:download" width={18} data-id="element-950" />} onClick={handleDownload} fullWidth data-id="element-949">
            Download PDF
          </Button>
          <Button variant="contained" startIcon={<Icon icon="lucide:printer" width={18} data-id="element-952" />} onClick={handlePrint} fullWidth data-id="element-951">
            Print
          </Button>
        </Box>
      </Box>
    </Drawer>;
};
export default EPRCViewerDrawer;